import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "./About";
import { CTABanner } from "../components/Sections";
import { AddOns } from "../components/NewSections";
import { pricingPlans, pricingFaqs } from "../mock";
import { CheckCircle2, Sparkles, ChevronDown, ChevronUp, ShieldCheck, Activity, ArrowRight, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useCart } from "../contexts/CartContext";
import { useToast } from "../hooks/use-toast";

const Pricing = () => {
  const [expanded, setExpanded] = useState(null);
  const { addItem } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAdd = (plan) => {
    addItem({
      id: `plan-${plan.name.toLowerCase().replace(/\s+/g, "-")}`,
      type: "plan",
      name: `${plan.name.replace(/_/g, " ")} Plan`,
      monthly: plan.oneTime ? 0 : plan.price,
      setupFee: plan.setupFee || 0,
      oneTime: plan.oneTime || 0,
      description: plan.setupLabel,
    });
    toast({ title: "Added to cart", description: `${plan.name} plan ready for checkout.` });
    navigate("/checkout");
  };

  return (
    <main>
      <PageHeader title="Pricing" subtitle="INVEST IN YOUR FUTURE" />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">PLANS</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy text-center">Choose the plan that fits your credit journey</h2>
            <p className="text-knight-navy/70 leading-relaxed mt-3">Transparent pricing. No long-term contracts. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
            {pricingPlans.map((plan, i) => {
              const isDIY = plan.name === "DIY DISPUTE LETTERS";
              const showBadge = plan.popular || isDIY;
              return (
              <div
                key={plan.name}
                className={`card-intense card-amplify ${plan.popular ? "card-amplify-popular" : ""} relative rounded-3xl p-8 flex flex-col knight-gradient text-white shadow-2xl card-intense-dark ${plan.popular ? "scale-[1.02]" : ""}`}
              >
                {showBadge && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-knight-gold text-knight-navy font-bold px-4 py-1 whitespace-nowrap">
                    <Sparkles size={12} className="mr-1" /> {plan.popular ? "Most Popular" : "Do It Yourself"}
                  </Badge>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-display text-2xl font-bold tracking-wide mb-3 text-knight-gold">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-bold">$</span>
                    <span className="font-display text-6xl font-bold">{plan.price}</span>
                    <span className="text-lg text-white/70">{plan.priceLabel}</span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm border bg-white/10 text-knight-gold border-white/15">
                    <ShieldCheck size={12} /> {plan.setupLabel}
                  </div>
                  {plan.biweekly && (
                    <p className="text-[11px] mt-2 font-semibold text-white/80">*Biweekly payments available</p>
                  )}
                  <p className="text-xs mt-3 text-white/75">{plan.description}</p>
                </div>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {plan.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 size={17} className="flex-shrink-0 mt-0.5 text-knight-gold" />
                      <span className="text-white/90">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  onClick={() => handleAdd(plan)}
                  className="w-full font-bold btn-lift bg-knight-gold hover:bg-knight-gold/90 text-knight-navy"
                >
                  <ShoppingCart size={16} className="mr-2" /> Add to Cart
                </Button>

                <button
                  onClick={() => isDIY ? null : setExpanded(expanded === i ? null : i)}
                  className={`flex items-center justify-center gap-1 w-full mt-4 text-xs font-semibold text-white/80 hover:text-knight-gold transition-colors ${isDIY ? "invisible pointer-events-none" : ""}`}
                  aria-hidden={isDIY ? "true" : "false"}
                  tabIndex={isDIY ? -1 : 0}
                >
                  Documents needed {expanded === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {!isDIY && expanded === i && (
                  <p className="text-xs leading-relaxed mt-3 p-3 rounded-lg bg-white/10 text-white/85">
                    {plan.documents}
                  </p>
                )}
              </div>
              );
            })}
          </div>

          <p className="text-center text-sm text-knight-navy/60 mt-10 max-w-2xl mx-auto">
            No hidden fees. Cancel anytime. By law, no credit repair company can guarantee specific score increases &mdash; we guarantee transparency, professionalism, and dedicated dispute work.
          </p>

          {/* Section transition divider */}
          <div className="my-16 relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-dashed border-knight-navy/15" />
            </div>
            <div className="relative flex justify-center">
              <div className="bg-white px-6 py-2 flex items-center gap-3 rounded-full border-2 border-knight-gold shadow-sm animate-pulse">
                <Activity size={14} className="text-knight-gold" />
                <span className="text-[11px] font-bold tracking-[0.25em] text-knight-navy uppercase">Required Add-on</span>
                <Activity size={14} className="text-knight-gold" />
              </div>
            </div>
          </div>

          {/* Credit monitoring requirement banner */}
          <div className="max-w-4xl mx-auto">
            <div className="card-intense group relative bg-gradient-to-br from-slate-50 to-white border border-slate-200 hover:border-knight-gold rounded-3xl px-8 py-10 text-center overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 cursor-pointer">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-knight-gold/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 mb-4">
                  <Activity size={22} className="text-knight-navy" />
                  <h3 className="font-display text-2xl lg:text-3xl font-bold text-knight-navy">Credit Monitoring Required for All Plans</h3>
                </div>
                <p className="text-knight-navy/75 leading-relaxed max-w-2xl mx-auto mb-6">
                  A SmartCredit subscription is required during the credit repair process of $29.99. Cancel anytime after successful completion of your credit repair journey.
                </p>

                {/* SmartCredit brand badge */}
                <div className="inline-flex items-center gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <div className="text-left leading-tight">
                    <p className="font-display font-extrabold text-lg text-knight-navy">Smart<span className="text-emerald-600">Credit</span></p>
                    <p className="text-[11px] text-knight-navy/55 font-semibold tracking-wide">Three-bureau monitoring</p>
                  </div>
                </div>

                <div>
                  <a
                    href="https://www.smartcredit.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-bold text-knight-gold hover:text-knight-navy transition-colors"
                  >
                    Set up SmartCredit monitoring <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AddOns />

      {/* Custom Packages CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative knight-gradient text-white rounded-3xl px-8 py-10 text-center overflow-hidden">
            <div className="absolute inset-0 grain-overlay opacity-50" />
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-knight-gold/15 blur-3xl" />
            <div className="relative">
              <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">CUSTOM PACKAGES</p>
              <h3 className="font-display text-3xl lg:text-4xl font-bold mb-3">Ask about our <em>custom packages</em></h3>
              <p className="text-white/80 max-w-xl mx-auto mb-6">Have a unique situation? Bundling multiple add-ons, family members, or a business profile? Damon will tailor a package that fits your goals and budget.</p>
              <a href="https://zcal.co/knightmediagroup/kcc1" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold btn-lift">
                  Talk to Damon About Custom Pricing
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faq" className="py-20 bg-white scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">FAQ</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-3 text-center whitespace-nowrap">Pricing &amp; Plan Questions</h2>
            <p className="text-knight-navy/70">Everything you need to know before getting started.</p>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {pricingFaqs.map((f, i) => (
              <AccordionItem key={i} value={`pf-${i}`} className="faq-hover border border-slate-200 rounded-xl px-5 data-[state=open]:border-knight-navy data-[state=open]:bg-slate-50/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold text-knight-navy hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-knight-navy/75 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABanner />
    </main>
  );
};

export default Pricing;
